import { useState, useEffect } from 'react'
import api from '../../services/api'
import {
  HiOutlineUserGroup,
  HiOutlinePlus,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineUsers,
} from 'react-icons/hi'
import toast from 'react-hot-toast'

function TeamManagement() {
  const [teams, setTeams] = useState([])
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editingTeam, setEditingTeam] = useState(null)
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    leader_id: '',
  })

  useEffect(() => {
    loadTeams()
    loadUsers()
  }, [])

  const loadTeams = async () => {
    setLoading(true)
    try {
      const response = await api.get('/admin/teams')
      if (response.data.status === 'success') {
        setTeams(response.data.data)
      }
    } catch (error) {
      console.error('Failed to load teams:', error)
      toast.error('Không thể tải danh sách nhóm')
    } finally {
      setLoading(false)
    }
  }

  const loadUsers = async () => {
    try {
      const response = await api.get('/admin/users', {
        params: { per_page: 100 }
      })
      if (response.data.status === 'success') {
        setUsers(response.data.data)
      }
    } catch (error) {
      console.error('Failed to load users:', error)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    try {
      if (editingTeam) {
        await api.put(`/admin/teams/${editingTeam.id}`, formData)
        toast.success('Đã cập nhật nhóm')
      } else {
        await api.post('/admin/teams', formData)
        toast.success('Đã tạo nhóm mới')
      }
      setShowModal(false)
      resetForm()
      loadTeams()
    } catch (error) {
      console.error('Failed to save team:', error)
      toast.error(error.response?.data?.message || 'Lỗi khi lưu')
    }
  }

  const handleEdit = (team) => {
    setEditingTeam(team)
    setFormData({
      name: team.name,
      description: team.description || '',
      leader_id: team.leader_id || '',
    })
    setShowModal(true)
  }

  const handleDelete = async (team) => {
    if (!confirm(`Xóa nhóm "${team.name}"? Nhóm phải không có thành viên.`)) return

    try {
      await api.delete(`/admin/teams/${team.id}`)
      toast.success('Đã xóa nhóm')
      loadTeams()
    } catch (error) {
      console.error('Failed to delete team:', error)
      toast.error(error.response?.data?.message || 'Không thể xóa nhóm')
    }
  }

  const resetForm = () => {
    setEditingTeam(null)
    setFormData({
      name: '',
      description: '',
      leader_id: '',
    })
  }

  const leaders = users.filter(u => u.role === 'leader' || u.role === 'admin')

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <HiOutlineUserGroup className="w-6 h-6 mr-2 text-green-500" />
            Quản lý nhóm
          </h1>
          <p className="text-gray-500 mt-1">
            Tổng cộng {teams.length} nhóm
          </p>
        </div>
        <button
          onClick={() => {
            resetForm()
            setShowModal(true)
          }}
          className="btn-primary"
        >
          <HiOutlinePlus className="w-5 h-5 mr-2" />
          Thêm nhóm
        </button>
      </div>

      {/* Teams Grid */}
      {loading ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="card p-6 animate-pulse">
              <div className="h-6 bg-gray-200 rounded w-1/2 mb-4" />
              <div className="h-4 bg-gray-200 rounded w-3/4" />
            </div>
          ))}
        </div>
      ) : teams.length === 0 ? (
        <div className="card p-12 text-center">
          <HiOutlineUserGroup className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">Chưa có nhóm nào</p>
          <button
            onClick={() => setShowModal(true)}
            className="btn-primary mt-4"
          >
            <HiOutlinePlus className="w-5 h-5 mr-2" />
            Tạo nhóm đầu tiên
          </button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {teams.map(team => (
            <div key={team.id} className="card p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <HiOutlineUserGroup className="w-6 h-6 text-green-600" />
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => handleEdit(team)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <HiOutlinePencil className="w-4 h-4 text-gray-500" />
                  </button>
                  <button
                    onClick={() => handleDelete(team)}
                    className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <HiOutlineTrash className="w-4 h-4 text-red-500" />
                  </button>
                </div>
              </div>

              <h3 className="font-semibold text-gray-900 text-lg">{team.name}</h3>
              {team.description && (
                <p className="text-sm text-gray-500 mt-1">{team.description}</p>
              )}

              <div className="mt-4 pt-4 border-t border-gray-100">
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center text-gray-500">
                    <HiOutlineUsers className="w-4 h-4 mr-1" />
                    {team.member_count} thành viên
                  </div>
                  {team.leader_name && (
                    <div className="text-primary-600 font-medium">
                      Leader: {team.leader_name}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl w-full max-w-md">
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-xl font-semibold text-gray-900">
                {editingTeam ? 'Sửa nhóm' : 'Thêm nhóm mới'}
              </h2>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tên nhóm
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="input"
                  required
                  placeholder="VD: Nhóm Đà Nẵng 1"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mô tả
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="input"
                  rows={3}
                  placeholder="Mô tả ngắn về nhóm..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Leader
                </label>
                <select
                  value={formData.leader_id}
                  onChange={(e) => setFormData({ ...formData, leader_id: e.target.value })}
                  className="input"
                >
                  <option value="">Chọn leader...</option>
                  {leaders.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.full_name} ({user.role})
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn-outline flex-1"
                >
                  Hủy
                </button>
                <button type="submit" className="btn-primary flex-1">
                  {editingTeam ? 'Cập nhật' : 'Tạo mới'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default TeamManagement
